import { FC, useCallback, useMemo } from 'react';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import clsx from 'clsx';
import { Sizes } from 'enums/sizes';
import { getSizeValue } from 'helpers/getSizesValue';
import PanelHeader from '../../Panel/PanelHeader';
import PanelBody from '../../Panel/PanelBody';
import PanelFooter from '../../Panel/PanelFooter';
import { OverlaySession, PanelActions, PanelConfig } from '../OverlayContext';
import { Z_INDEX } from 'shared/constants/zIndex';

const sizes = {
  [Sizes.xs]: 'sm:max-w-xs',
  [Sizes.sm]: 'sm:max-w-sm',
  [Sizes.md]: 'sm:max-w-md',
  [Sizes.lg]: 'sm:max-w-lg',
  [Sizes.xl]: 'sm:max-w-xl',
  [Sizes['2xl']]: 'sm:max-w-2xl',
  [Sizes['3xl']]: 'sm:max-w-3xl',
  [Sizes['4xl']]: 'sm:max-w-4xl',
  [Sizes['5xl']]: 'sm:max-w-5xl',
  [Sizes['7xl']]: 'sm:max-w-7xl',
};

interface PanelRendererProps {
  sessions: OverlaySession[];
  closePanel: () => void;
  onTransitionEnd: (id: string) => void;
}

export const PanelRenderer: FC<PanelRendererProps> = ({ sessions, closePanel, onTransitionEnd }) => {
  const lastActiveSession = sessions.filter(s => !s.closing).pop();

  return (
    <>
      {sessions.map((session, index) => (
        <PanelItem
          key={session.id}
          session={session}
          index={index}
          isLast={session === lastActiveSession}
          closePanel={closePanel}
          onTransitionEnd={onTransitionEnd}
        />
      ))}
    </>
  );
};

interface PanelItemProps {
  session: OverlaySession;
  index: number;
  isLast: boolean;
  closePanel: () => void;
  onTransitionEnd: (id: string) => void;
}

const PanelItem: FC<PanelItemProps> = ({ session, index, isLast, closePanel, onTransitionEnd }) => {
  const config = session.config as PanelConfig;
  const actions = session.actions as PanelActions | undefined;
  const sizeValue = getSizeValue(config.size, sizes);
  const zIndex = Z_INDEX.PANEL_BASE + index * Z_INDEX.PANEL_STEP;

  const handleOnDelete = useCallback(() => {
    actions?.onDelete?.();
  }, [actions]);

  const handleTransitionEnd = useCallback((e: React.TransitionEvent) => {
    if (session.closing && e.target === e.currentTarget && e.propertyName === 'transform') {
      onTransitionEnd(session.id);
    }
  }, [session.closing, session.id, onTransitionEnd]);

  const renderFooter = useMemo(() => {
    if (!actions) return null;
    return (
      <PanelFooter
        {...actions}
        isLoading={false}
        onDelete={actions.onDelete ? handleOnDelete : undefined}
        onClose={closePanel}
      />
    );
  }, [actions, closePanel, handleOnDelete]);

  return (
    <Dialog
      open={!session.closing}
      onClose={isLast ? closePanel : () => {}}
      className="relative"
      style={{ zIndex }}
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 flex w-screen justify-center overflow-y-auto bg-zinc-950/25 px-2 py-2 transition duration-100 focus:outline-0 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in sm:px-6 sm:py-8 lg:px-8 lg:py-16"
        style={{ zIndex }}
      />
      <div className="fixed inset-0 overflow-hidden" style={{ zIndex: zIndex + 1 }}>
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
            <DialogPanel
              transition
              className={clsx(
                sizeValue,
                'pointer-events-auto w-screen max-w-md transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700'
              )}
              onTransitionEnd={handleTransitionEnd}
            >
              <div className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl">
                <PanelHeader title={config.title} onClose={closePanel} />
                <PanelBody isLoading={false}>{session.content}</PanelBody>
                {renderFooter}
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
